<div class="dashboard">

    <!-- =====================================================
         HEADER
         ===================================================== -->

    <div class="dashboard-header">

        <div class="dashboard-title">

            <h1>
                Dashboard
            </h1>

            <p>
                Overview of file transformations and
                external API processing.
            </p>

        </div>


        <div class="dashboard-filters">

            <!-- COMPANY -->

            <div class="dashboard-filter">

                <i class="pi pi-building"></i>

                <select
                    (change)="
                        onCompanyChange(
                            +$any($event.target).value
                        )
                    "
                >

                    @for (
                        company of companies();
                        track company.id
                    ) {

                        <option
                            [value]="company.id"
                            [selected]="
                                company.id === selectedCompanyId()
                            "
                        >
                            {{ company.name }}
                        </option>

                    }

                </select>

                <i class="pi pi-chevron-down"></i>

            </div>


            <!-- MARKET -->

            <div class="dashboard-filter">

                <i class="pi pi-map-marker"></i>

                <select
                    [value]="selectedMarketId() ?? 'all'"
                    (change)="                    
                        onMarketChange(
                            $any($event.target).value
                        )
                    "
                >
                     <option value="all">
                        All markets
                    </option>

                    @for (
                        market of markets();
                        track market.id
                    ) {

                        <option
                            [value]="market.id"
                            [selected]="
                                market.id === selectedMarketId()
                            "
                        >
                            {{ market.code }} — {{ market.name }}
                        </option>

                    }

                </select>

                <i class="pi pi-chevron-down"></i>

            </div>


            <!-- PERIOD -->

            <div class="dashboard-filter">

                <i class="pi pi-calendar"></i>

                <select
                    (change)="
                        onPeriodChange(
                            +$any($event.target).value
                        )
                    "
                >

                    <option
                        value="7"
                        [selected]="selectedDays() === 7"
                    >
                        Last 7 days
                    </option>

                    <option
                        value="30"
                        [selected]="selectedDays() === 30"
                    >
                        Last 30 days
                    </option>

                    <option
                        value="90"
                        [selected]="selectedDays() === 90"
                    >
                        Last 90 days
                    </option>

                </select>

                <i class="pi pi-chevron-down"></i>

            </div>

        </div>

    </div>


    <!-- =====================================================
         LOADING
         ===================================================== -->

    @if (loading()) {

        <div class="dashboard-state loading-state">

            <i class="pi pi-spin pi-spinner"></i>

            <span>
                Loading dashboard...
            </span>

        </div>

    }


    <!-- =====================================================
         ERROR
         ===================================================== -->

    @else if (error()) {

        <div class="dashboard-state error-state">

            <i class="pi pi-exclamation-circle"></i>

            <div>

                <strong>
                    Unable to load dashboard
                </strong>

                <span>
                    {{ error() }}
                </span>

            </div>

        </div>

    }


    <!-- =====================================================
         DASHBOARD
         ===================================================== -->

    @else if (dashboard(); as data) {


        <!-- =================================================
             KPI
             ================================================= -->

        <div class="kpi-grid">


            <!-- FILES -->

            <div class="kpi-card">

                <div class="kpi-icon">

                    <i class="pi pi-file"></i>

                </div>

                <div class="kpi-content">

                    <span class="kpi-label">
                        Files transformed
                    </span>

                    <strong class="kpi-value">
                        {{ data.summary.processed | number }}
                    </strong>

                    <small>
                        {{ selectedMarket()?.name || 'Selected market' }}
                    </small>

                </div>

            </div>


            <!-- TRANSFORMATION SUCCESS -->

            <div class="kpi-card">

                <div class="kpi-icon success">

                    <i class="pi pi-check-circle"></i>

                </div>

                <div class="kpi-content">

                    <span class="kpi-label">
                        Transformation success
                    </span>

                    <strong class="kpi-value">

                        @if (data.summary.received > 0) {

                            {{
                                (
                                    data.summary.processed /
                                    data.summary.received *
                                    100
                                ) | number:'1.1-1'
                            }}%

                        } @else {

                            0%

                        }

                    </strong>


                    <div class="progress">

                        <div
                            class="progress-value success-progress"
                            [style.width.%]="
                                data.summary.received > 0
                                    ? (
                                        data.summary.processed /
                                        data.summary.received *
                                        100
                                    )
                                    : 0
                            "
                        ></div>

                    </div>

                </div>

            </div>


            <!-- API SUCCESS -->

            <div class="kpi-card">

                <div class="kpi-icon info">

                    <i class="pi pi-send"></i>

                </div>

                <div class="kpi-content">

                    <span class="kpi-label">
                        MARKANT API success
                    </span>

                    <strong class="kpi-value">
                        {{
                            getApiSuccessRate(data)
                                | number:'1.1-1'
                        }}%
                    </strong>


                    <div class="progress">

                        <div
                            class="progress-value info-progress"
                            [style.width.%]="
                                getApiSuccessRate(data)
                            "
                        ></div>

                    </div>

                </div>

            </div>


            <!-- FAILED -->

            <div class="kpi-card">

                <div class="kpi-icon danger">

                    <i class="pi pi-exclamation-triangle"></i>

                </div>

                <div class="kpi-content">

                    <span class="kpi-label">
                        Failed transformations
                    </span>

                    <strong class="kpi-value">
                        {{ data.summary.processing_failed | number }}
                    </strong>

                    <small>
                        Selected period
                    </small>

                </div>

            </div>

        </div>



        <!-- =================================================
             PROCESSING + API
             ================================================= -->

        <div class="dashboard-grid">


            <!-- PROCESSING ACTIVITY -->

            <div class="dashboard-card activity-card">

                <div class="card-header">

                    <div>

                        <h2>
                            Processing activity
                        </h2>

                        <p>
                            File transformation activity
                            during the selected period.
                        </p>

                    </div>

                    <span class="card-icon">
                        <i class="pi pi-chart-bar"></i>
                    </span>

                </div>


                @if (data.processing_activity.length > 0) {

                    <div class="activity-chart">

                        @for (
                            item of data.processing_activity;
                            track item.date
                        ) {

                            <div class="chart-column">

                                <div class="chart-value">
                                    {{ item.received }}
                                </div>


                                <div class="chart-bars">

                                    <div
                                        class="chart-bar received"
                                        [style.height.%]="
                                            getActivityHeight(
                                                item.received
                                            )
                                        "
                                        [title]="
                                            'Received: ' +
                                            item.received
                                        "
                                    ></div>


                                    <div
                                        class="chart-bar processed"
                                        [style.height.%]="
                                            getActivityHeight(
                                                item.processed
                                            )
                                        "
                                        [title]="
                                            'Processed: ' +
                                            item.processed
                                        "
                                    ></div>


                                    <div
                                        class="chart-bar failed"
                                        [style.height.%]="
                                            getActivityHeight(
                                                item.failed
                                            )
                                        "
                                        [title]="
                                            'Failed: ' +
                                            item.failed
                                        "
                                    ></div>

                                </div>


                                <span class="chart-label">

                                    {{
                                        item.date
                                            | date:'dd.MM.'
                                    }}

                                </span>

                            </div>

                        }

                    </div>


                    <div class="chart-legend">

                        <span>
                            <i class="received-dot"></i>
                            Received
                        </span>

                        <span>
                            <i class="processed-dot"></i>
                            Processed
                        </span>

                        <span>
                            <i class="failed-dot"></i>
                            Failed
                        </span>

                    </div>

                } @else {

                    <div class="empty-state chart-empty">

                        <i class="pi pi-chart-bar"></i>

                        <strong>
                            No processing activity
                        </strong>

                        <span>
                            No files were processed during
                            the selected period.
                        </span>

                    </div>

                }

            </div>



            <!-- EXTERNAL API -->

            <div class="dashboard-card api-card">

                <div class="card-header">

                    <div>

                        <h2>
                            MARKANT API
                        </h2>

                        <p>
                            Archive delivery during
                            the selected period.
                        </p>

                    </div>

                    <span class="card-icon">
                        <i class="pi pi-send"></i>
                    </span>

                </div>


                <div class="api-summary">

                    <div class="api-rate">

                        <div
                            class="circle-progress"
                            [style.--progress]="
                                getApiSuccessRate(data) + '%'
                            "
                        >

                            <span>
                                {{
                                    getApiSuccessRate(data)
                                        | number:'1.1-1'
                                }}%
                            </span>

                        </div>


                        <div class="api-rate-info">

                            <strong>
                                Success rate
                            </strong>

                            <span>
                                External API delivery
                            </span>

                        </div>

                    </div>


                    <div class="api-stats">

                        <div class="api-stat">

                            <span>
                                Sent
                            </span>

                            <strong class="success-text">
                                {{
                                    data.api_delivery.sent
                                        | number
                                }}
                            </strong>

                        </div>


                        <div class="api-stat">

                            <span>
                                Retrying
                            </span>

                            <strong>
                                {{
                                    data.api_delivery.retrying
                                        | number
                                }}
                            </strong>

                        </div>


                        <div class="api-stat">

                            <span>
                                Failed
                            </span>

                            <strong class="danger-text">
                                {{
                                    data.api_delivery.failed
                                        | number
                                }}
                            </strong>

                        </div>


                        <div class="api-stat">

                            <span>
                                Not queued
                            </span>

                            <strong>
                                {{
                                    data.api_delivery.not_queued
                                        | number
                                }}
                            </strong>

                        </div>

                    </div>

                </div>


                <div class="api-status">

                    @if (data.api_delivery.failed === 0) {

                        <span class="status success-status">

                            <i class="pi pi-check-circle"></i>

                            API delivery operational

                        </span>

                    } @else {

                        <span class="status danger-status">

                            <i class="pi pi-exclamation-circle"></i>

                            API delivery has failures

                        </span>

                    }

                </div>

            </div>

        </div>



        <!-- =================================================
             MARKET OVERVIEW
             ================================================= -->

        <div class="dashboard-card market-card">

            <div class="card-header">

                <div>

                    <h2>
                        Market overview
                    </h2>

                    <p>
                        Performance across markets
                        for the selected company.
                    </p>

                </div>

                <span class="card-icon">

                    <i class="pi pi-map-marker"></i>

                </span>

            </div>


            @if (data.markets.length > 0) {

                <div class="market-table">

                    <div class="table-header">

                        <span>
                            Market
                        </span>

                        <span>
                            Files
                        </span>

                        <span>
                            Success rate
                        </span>

                        <span>
                            Status
                        </span>

                    </div>


                    @for (
                        market of data.markets;
                        track market.market_id
                    ) {

                        <div class="table-row">


                            <div class="market-name">

                                <span class="market-code">
                                    {{ market.market_code }}
                                </span>

                                <span>
                                    {{ market.market_name }}
                                </span>

                            </div>


                            <strong>
                                {{ market.files | number }}
                            </strong>


                            <div class="rate">

                                <div class="rate-value">

                                    <span>
                                        {{
                                            market.success_rate
                                                | number:'1.1-1'
                                        }}%
                                    </span>

                                </div>


                                <div class="mini-progress">

                                    <div
                                        [style.width.%]="
                                            market.success_rate
                                        "
                                    ></div>

                                </div>

                            </div>


                            <span
                                class="status"
                                [class]="
                                    getMarketStatusClass(
                                        market.status
                                    )
                                "
                            >

                                <i
                                    [class]="
                                        getMarketStatusIcon(
                                            market.status
                                        )
                                    "
                                ></i>

                                {{ market.status }}

                            </span>

                        </div>

                    }

                </div>

            } @else {

                <div class="empty-state">

                    <i class="pi pi-map-marker"></i>

                    <strong>
                        No market data
                    </strong>

                    <span>
                        There is no market activity
                        for the selected period.
                    </span>

                </div>

            }

        </div>



        <!-- =================================================
             RECENT FAILURES
             ================================================= -->

        <div class="dashboard-card errors-card">

            <div class="card-header">

                <div>

                    <h2>
                        Recent failures
                    </h2>

                    <p>
                        Latest processing and API failures
                        requiring attention.
                    </p>

                </div>


                <button
                    type="button"
                    class="card-action"
                >
                    View all failures

                    <i class="pi pi-arrow-right"></i>

                </button>

            </div>


            @if (data.recent_failures.length > 0) {

                <div class="errors-table">

                    <div class="error-row error-header">

                        <span>
                            Time
                        </span>

                        <span>
                            File
                        </span>

                        <span>
                            Stage
                        </span>

                        <span>
                            Error
                        </span>

                        <span>
                            Exception
                        </span>

                        <span></span>

                        <span></span>

                    </div>


                    @for (
                        failure of data.recent_failures;
                        track failure.file_id + '-' + failure.created_at
                    ) {

                        <div class="error-row">


                            <span class="error-time">

                                {{
                                    failure.created_at
                                        | date:'dd.MM. HH:mm'
                                }}

                            </span>


                            <span
                                class="file-name"
                                [title]="failure.filename"
                            >
                                {{ failure.filename }}
                            </span>


                            <span class="status warning-status">

                                {{ failure.stage }}

                            </span>


                            <span
                                class="error-message"
                                [title]="
                                    failure.error_message ||
                                    'Unknown error'
                                "
                            >

                                {{
                                    failure.error_message ||
                                    'Unknown error'
                                }}

                            </span>


                            <span>
                                {{ failure.exception_type || '—' }}
                            </span>


                            <a
                                [routerLink]="[
                                    '/files',
                                    failure.file_id
                                ]"
                                class="details-link"
                            >

                                Details

                                <i class="pi pi-arrow-right"></i>

                            </a>

                            <span class="recent-file-actions">
                                <button
                                    type="button"
                                    class="file-download-button"
                                    title="Download failed file"
                                    aria-label="Download failed file"
                                    (click)="downloadInputFile(failure.file_id); $event.stopPropagation()"
                                >
                                    <i class="pi pi-download"></i>
                                </button>
                            </span>

                        </div>

                    }

                </div>

            } @else {

                <div class="empty-state errors-empty">

                    <i class="pi pi-check-circle"></i>

                    <strong>
                        No recent failures
                    </strong>

                    <span>
                        Everything looks good for
                        the selected period.
                    </span>

                </div>

            }

        </div>

        <!-- =================================================
            RECENT INPUT FILES
            ================================================= -->

        <div class="dashboard-card recent-files-card">

            <div class="card-header">

                <div>
                    <h2>
                        Recent input files
                    </h2>

                    <p>
                        Latest files received for the selected market.
                    </p>
                </div>

                <span class="card-icon">
                    <i class="pi pi-file"></i>
                </span>

            </div>


            <!-- LOADING -->

            @if (recentFilesLoading()) {

                <div class="empty-state">

                    <i class="pi pi-spin pi-spinner"></i>

                    <strong>
                        Loading input files...
                    </strong>

                </div>

            }


            <!-- ERROR -->

            @else if (recentFilesError()) {

                <div class="empty-state">

                    <i class="pi pi-exclamation-circle"></i>

                    <strong>
                        Unable to load input files
                    </strong>

                    <span>
                        {{ recentFilesError() }}
                    </span>

                    <button
                        type="button"
                        class="card-action"
                        (click)="loadRecentInputFiles()"
                    >
                        Retry
                        <i class="pi pi-refresh"></i>
                    </button>

                </div>

            }


            <!-- EMPTY -->

            @else if (recentInputFiles().length === 0) {

                <div class="empty-state">

                    <i class="pi pi-folder-open"></i>

                    <strong>
                        No input files
                    </strong>

                    <span>
                        No input files were found for the selected market.
                    </span>

                </div>

            }


            <!-- FILES -->

            @else {

                <div class="recent-files-table">

                    <div class="recent-files-row recent-files-header">

                        <span>
                            Received
                        </span>

                        <span>
                            Filename
                        </span>

                        <span>
                            Processing status
                        </span>
                        <span>  

                        </span>

                    </div>


                    @for (
                        file of recentInputFiles();
                        track file.id
                    ) {

                        <div
                            class="recent-files-row recent-file-clickable"
                            (click)="openFileDetails(file.id)"
                            (keydown.enter)="openFileDetails(file.id)"
                            (keydown.space)="openFileDetails(file.id); $event.preventDefault()"
                            tabindex="0"
                            role="button"
                            [attr.aria-label]="'View details for ' + file.filename"
                        >

                            <span class="recent-file-date">
                                {{
                                    file.created_at
                                        | date:'dd.MM.yyyy HH:mm'
                                }}
                            </span>

                            <span
                                class="file-name"
                                [title]="file.filename"
                            >
                                <i class="pi pi-file"></i>
                                {{ file.filename }}
                            </span>

                            <span class="status">
                                {{ file.processing_status }}
                            </span>

                            <span class="recent-file-actions">
                                <button
                                    type="button"
                                    class="file-download-button"
                                    title="Download input file"
                                    aria-label="Download input file"
                                    (click)="downloadInputFile(file.id); $event.stopPropagation()"
                                >
                                    <i class="pi pi-download"></i>
                                </button>

                                <button
                                    type="button"
                                    class="file-download-button"
                                    title="Download output archive"
                                    aria-label="Download output archive"
                                    (click)="downloadOutputArchive(file.id); $event.stopPropagation()"
                                >
                                    <i class="pi pi-file-export"></i>
                                </button>

                                @if (file.processing_status === 'PROCESSED') {
                                    <button
                                        type="button"
                                        class="file-download-button"
                                        title="Send output file to API"
                                        aria-label="Send output file to API"
                                        (click)="confirmSendOutputArchiveToApi(file); $event.stopPropagation()"
                                    >
                                        <i class="pi pi-cloud-upload"></i>
                                    </button>
                                }

                                @if (file.processing_status === 'PROCESSED' || file.processing_status === 'FAILED') {
                                    <button
                                        type="button"
                                        class="file-download-button"
                                        title="Reprocess file"
                                        aria-label="Reprocess file"
                                        (click)="confirmReprocessFile(file); $event.stopPropagation()"
                                    >
                                        <i class="pi pi-refresh"></i>
                                    </button>
                                }
                            </span>

                        </div>

                    }

                </div>


                <!-- PAGINATION -->

                <div class="recent-files-pagination">

                    <span class="pagination-info">

                        @if (recentFilesTotal() > 0) {

                            Showing
                            {{ recentFilesOffset() + 1 }}–
                            {{
                                Math.min(
                                    recentFilesOffset() + recentFilesLimit(),
                                    recentFilesTotal()
                                )
                            }}
                            of {{ recentFilesTotal() }}

                        }

                    </span>


                    <div class="pagination-controls">

                        <button
                            type="button"
                            class="pagination-button"
                            [disabled]="
                                recentFilesCurrentPage() <= 1 ||
                                recentFilesLoading()
                            "
                            (click)="
                                onRecentFilesPageChange(
                                    recentFilesCurrentPage() - 1
                                )
                            "
                        >
                            <i class="pi pi-chevron-left"></i>
                            Previous
                        </button>


                        <span class="pagination-page">
                            Page {{ recentFilesCurrentPage() }}
                            of {{ recentFilesTotalPages() }}
                        </span>


                        <button
                            type="button"
                            class="pagination-button"
                            [disabled]="
                                recentFilesCurrentPage() >=
                                    recentFilesTotalPages() ||
                                recentFilesLoading()
                            "
                            (click)="
                                onRecentFilesPageChange(
                                    recentFilesCurrentPage() + 1
                                )
                            "
                        >
                            Next
                            <i class="pi pi-chevron-right"></i>
                        </button>

                    </div>

                </div>

            }

        </div>

        <!-- ================================================= -->

    }


    <!-- =====================================================
     INPUT FILE DETAILS POPOUT
     ===================================================== -->

    @if (
        selectedFileDetails() !== null ||
        detailsLoading() ||
        detailsError() !== null
    ) {
        <div
            class="file-details-backdrop"
            (click)="closeFileDetails()"
        >
            <section
                class="file-details-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="file-details-title"
                (click)="$event.stopPropagation()"
            >
                <!-- HEADER -->

                <div class="file-details-header">
                    <div class="file-details-heading">
                        <span class="file-details-icon">fai
                            <i class="pi pi-file"></i>
                        </span>

                        <div>
                            <h2 id="file-details-title">
                                Input file details
                            </h2>

                            @if (selectedFileDetails(); as file) {
                                <p>{{ file.filename }}</p>
                            }
                        </div>
                    </div>

                     @if (selectedFileDetails(); as file) {
                        <button
                            type="button"
                            class="file-details-download"
                            (click)="downloadInputFile(file.id)"
                            title="Download input file"
                        >
                            <i class="pi pi-download"></i>
                            Download
                        </button>
                    }

                    <button
                        type="button"
                        class="file-details-close"
                        aria-label="Close file details"
                        (click)="closeFileDetails()"
                    >
                        <i class="pi pi-times"></i>
                    </button>
                </div>

                <!-- LOADING -->

                @if (detailsLoading()) {
                    <div class="file-details-state">
                        <i class="pi pi-spin pi-spinner"></i>
                        <span>Loading file details...</span>
                    </div>
                }

                <!-- ERROR -->

                @else if (detailsError()) {
                    <div class="file-details-state error-state">
                        <i class="pi pi-exclamation-circle"></i>
                        <span>{{ detailsError() }}</span>

                        <button
                            type="button"
                            class="card-action"
                            [disabled]="selectedFileId() === null"
                            (click)="
                                selectedFileId() !== null &&
                                openFileDetails(selectedFileId()!)
                            "
                        >
                            Retry
                        </button>
                    </div>
                }

                <!-- DETAILS -->

                @else if (selectedFileDetails(); as file) {
                    <div class="file-details-content">

                        <!-- FILE -->

                        <section class="file-details-section">
                            <h3>File information</h3>

                            <div class="file-details-grid">
                                <div class="file-detail-item">
                                    <span>Filename</span>
                                    <strong>{{ file.filename }}</strong>
                                </div>

                                <div class="file-detail-item">
                                    <span>Received</span>
                                    <strong>
                                        {{ file.created_at | date:'dd.MM.yyyy HH:mm:ss' }}
                                    </strong>
                                </div>

                                <div class="file-detail-item">
                                    <span>Processing status</span>
                                    <strong class="status">
                                        {{ file.processing_status }}
                                    </strong>
                                </div>

                                <div class="file-detail-item">
                                    <span>API status</span>
                                    <strong class="status">
                                        {{ file.api_status }}
                                    </strong>
                                </div>

                                <div class="file-detail-item">
                                    <span>File size</span>
                                    <strong>
                                        {{ file.size_bytes | number }} bytes
                                    </strong>
                                </div>

                                <div class="file-detail-item">
                                    <span>Attempts</span>
                                    <strong>{{ file.attempt_count }}</strong>
                                </div>

                                <div class="file-detail-item full-width">
                                    <span>SHA-256</span>
                                    <code>{{ file.sha256 || '—' }}</code>
                                </div>
                            </div>

                            @if (file.last_error_message) {
                                <div class="file-details-error">
                                    <strong>
                                        {{ file.last_error_code || 'Processing error' }}
                                    </strong>
                                    <p>{{ file.last_error_message }}</p>
                                </div>
                            }
                        </section>

                        <!-- TRANSFORMATION -->

                        <section class="file-details-section">
                            <h3>Transformation</h3>

                            @if (file.transformation; as transformation) {
                                <div class="file-details-grid">
                                    <div class="file-detail-item">
                                        <span>Status</span>
                                        <strong class="status">
                                            {{ transformation.status }}
                                        </strong>
                                    </div>

                                    <div class="file-detail-item">
                                        <span>Completed</span>
                                        <strong>
                                            {{
                                                transformation.completed_at
                                                    ? (transformation.completed_at | date:'dd.MM.yyyy HH:mm:ss')
                                                    : '—'
                                            }}
                                        </strong>
                                    </div>

                                    <div class="file-detail-item">
                                        <span>Company</span>
                                        <strong>{{ transformation.company.name }}</strong>
                                    </div>

                                    <div class="file-detail-item">
                                        <span>Market</span>
                                        <strong>{{ transformation.market.name }}</strong>
                                    </div>

                                    <div class="file-detail-item">
                                        <span>Rule set</span>
                                        <strong>
                                            {{ transformation.rule_set_version.name }}
                                        </strong>
                                    </div>

                                    <div class="file-detail-item">
                                        <span>Rule set version</span>
                                        <strong>
                                            {{ transformation.rule_set_version.version_number }}
                                        </strong>
                                    </div>
                                </div>

                                <div class="file-details-result">

                                    <div class="transformation-result-header">
                                        <h4>Transformation result</h4>

                                        <button
                                            type="button"
                                            class="raw-json-button"
                                            (click)="toggleRawTransformationResult()"
                                        >
                                            <i
                                                class="pi"
                                                [class.pi-code]="!showRawTransformationResult()"
                                                [class.pi-list]="showRawTransformationResult()"
                                            ></i>

                                            {{ showRawTransformationResult()
                                                ? 'View summary'
                                                : 'View raw JSON' }}
                                        </button>
                                    </div>

                                    @if (transformationResult(); as result) {

                                        @if (showRawTransformationResult()) {

                                            <pre class="transformation-raw-json">{{ transformation.result | json }}</pre>

                                        } @else {

                                            <!-- SUMMARY -->

                                            <div class="transformation-result-summary">

                                                <div class="transformation-summary-status">
                                                    <span
                                                        class="transformation-status-badge"
                                                        [class.success]="result.status === 'SUCCESS'"
                                                        [class.failed]="result.status === 'FAILED'"
                                                    >
                                                        <i
                                                            class="pi"
                                                            [class.pi-check-circle]="result.status === 'SUCCESS'"
                                                            [class.pi-times-circle]="result.status === 'FAILED'"
                                                            [class.pi-info-circle]="
                                                                result.status !== 'SUCCESS' &&
                                                                result.status !== 'FAILED'
                                                            "
                                                        ></i>

                                                        {{ result.status || 'Status unavailable' }}
                                                    </span>
                                                </div>

                                                <div class="transformation-summary-counters">

                                                    <div class="transformation-counter error-counter">
                                                        <span>Errors</span>
                                                        <strong>{{ result.errors.length }}</strong>
                                                    </div>

                                                    <div class="transformation-counter warning-counter">
                                                        <span>Warnings</span>
                                                        <strong>{{ result.warnings.length }}</strong>
                                                    </div>

                                                </div>
                                            </div>

                                            <!-- ERRORS -->

                                            @if (result.errors.length > 0) {

                                                <div class="transformation-issues-section">

                                                    <h5>
                                                        <i class="pi pi-times-circle"></i>
                                                        Errors
                                                    </h5>

                                                    <div class="transformation-issue-list">

                                                        @for (issue of result.errors; track $index) {

                                                            <article class="transformation-issue error-issue">

                                                                <div class="transformation-issue-heading">

                                                                    <span class="issue-row">
                                                                        Row {{ issue.row ?? '—' }}
                                                                    </span>

                                                                    <code class="issue-code">
                                                                        {{ issue.code || 'ERROR' }}
                                                                    </code>

                                                                </div>

                                                                <p class="transformation-issue-message">
                                                                    {{ issue.message || 'No message provided.' }}
                                                                </p>

                                                                <div class="transformation-issue-fields">

                                                                    <div>
                                                                        <span>Input column</span>
                                                                        <strong>
                                                                            {{ issue.input_column || '—' }}
                                                                        </strong>
                                                                    </div>

                                                                    <div>
                                                                        <span>Output column</span>
                                                                        <code>
                                                                            {{ issue.output_column || '—' }}
                                                                        </code>
                                                                    </div>

                                                                </div>

                                                            </article>

                                                        }

                                                    </div>

                                                </div>

                                            }

                                            <!-- WARNINGS -->

                                            @if (result.warnings.length > 0) {

                                                <div class="transformation-issues-section">

                                                    <h5>
                                                        <i class="pi pi-exclamation-triangle"></i>
                                                        Warnings
                                                        <span class="issue-total">
                                                            {{ result.warnings.length }}
                                                        </span>
                                                    </h5>

                                                    <div class="transformation-warning-groups">

                                                        @for (
                                                            group of transformationWarningGroups();
                                                            track group.inputColumn
                                                        ) {

                                                            <details class="transformation-warning-group">

                                                                <summary>

                                                                    <span class="warning-group-name">
                                                                        {{ group.inputColumn }}
                                                                    </span>

                                                                    <span class="warning-group-count">
                                                                        {{ group.warnings.length }}
                                                                    </span>

                                                                    <i class="pi pi-chevron-down warning-group-chevron"></i>

                                                                </summary>

                                                                <div class="transformation-issue-list">

                                                                    @for (
                                                                        issue of group.warnings;
                                                                        track $index
                                                                    ) {

                                                                        <article class="transformation-issue">

                                                                            <div class="transformation-issue-heading">

                                                                                <span class="issue-row">
                                                                                    Row {{ issue.row ?? '—' }}
                                                                                </span>

                                                                                <code class="issue-code">
                                                                                    {{ issue.code || 'WARNING' }}
                                                                                </code>

                                                                            </div>

                                                                            <p class="transformation-issue-message">
                                                                                {{ issue.message || 'No message provided.' }}
                                                                            </p>

                                                                            <div class="transformation-issue-fields">

                                                                                <div>
                                                                                    <span>Output column</span>
                                                                                    <code>
                                                                                        {{ issue.output_column || '—' }}
                                                                                    </code>
                                                                                </div>

                                                                            </div>

                                                                        </article>

                                                                    }

                                                                </div>

                                                            </details>

                                                        }

                                                    </div>

                                                </div>

                                            }

                                            @if (
                                                result.errors.length === 0 &&
                                                result.warnings.length === 0
                                            ) {

                                                <div class="transformation-result-empty">
                                                    <i class="pi pi-check-circle"></i>
                                                    <span>No errors or warnings were reported.</span>
                                                </div>

                                            }

                                        }

                                    } @else {

                                        <div class="transformation-result-empty">
                                            <i class="pi pi-info-circle"></i>
                                            <span>No structured transformation result is available.</span>
                                        </div>

                                    }

                                </div>
                            } @else {
                                <div class="file-details-notice">
                                    <i class="pi pi-info-circle"></i>
                                    <span>
                                        No transformation is available for this file yet.
                                    </span>
                                </div>
                            }
                        </section>

                        <!-- EVENTS -->

                        <section class="file-details-section">
                            <h3>Event history</h3>

                            @if (file.events.length > 0) {
                                <div class="file-event-list">
                                    @for (
                                        event of file.events;
                                        track event.id
                                    ) {
                                        <div class="file-event">
                                            <div class="file-event-marker"></div>

                                            <div class="file-event-content">
                                                <div class="file-event-heading">
                                                    <strong>{{ event.event_type }}</strong>
                                                    <time>
                                                        {{
                                                            event.created_at
                                                                | date:'dd.MM.yyyy HH:mm:ss'
                                                        }}
                                                    </time>
                                                </div>

                                                @if (event.message) {
                                                    <p>{{ event.message }}</p>
                                                }

                                                @if (event.error_message) {
                                                    <p class="danger-text">
                                                        {{ event.error_message }}
                                                    </p>
                                                }

                                                @if (event.details) {
                                                    <details>
                                                        <summary>Event details</summary>
                                                        <pre>{{ event.details | json }}</pre>
                                                    </details>
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            } @else {
                                <div class="file-details-notice">
                                    <i class="pi pi-info-circle"></i>
                                    <span>No events recorded for this file.</span>
                                </div>
                            }
                        </section>
                    </div>
                }
            </section>
        </div>
    }

</div>